/**
 * ResearchListItem - Display component for a single research item in the list.
 */

import { useMemo } from 'react';

import { BadgeWithDot } from '@/lib/base/badges/badges';
import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

import { getResearchOutcome, getResearchStatusBadge } from './researchOutcome';
import type { Research } from './useResearch';

interface ResearchListItemProps {
  /** Research item to display */
  research: Research;
  /** Whether this item is currently selected */
  isSelected: boolean;
  /** Called when item is clicked */
  onClick: () => void;
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '…';
}

/**
 * Component for displaying a single research item in the list.
 */
export function ResearchListItem({
  research,
  isSelected,
  onClick,
}: ResearchListItemProps) {
  const outcome = getResearchOutcome(research);
  const { color: statusColor, label: statusLabel } =
    getResearchStatusBadge(outcome);

  const relativeTime = useMemo(() => {
    if (research.completed_at) {
      return `Researched ${formatRelativeTime(research.completed_at)}`;
    }
    if (research.created_at) {
      return `Started ${formatRelativeTime(research.created_at)}`;
    }
    return null;
  }, [research.completed_at, research.created_at]);

  const summarySnippet = useMemo(() => {
    if (!research.summary) {
      if (outcome === 'in_progress') {
        return 'Research in progress…';
      }
      if (outcome === 'queued') {
        return 'Waiting to start…';
      }
      if (outcome === 'needs_more_info') {
        return 'Not enough detail on this record to research it';
      }
      if (outcome === 'failed') {
        return 'Research failed';
      }
      return 'No summary available';
    }
    return truncateText(research.summary, 100);
  }, [research.summary, outcome]);

  return (
    <button
      type='button'
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-colors cursor-pointer ${
        isSelected
          ? 'bg-utility-brand-50 border-utility-brand-200'
          : 'bg-primary border-primary hover:bg-secondary'
      }`}
    >
      <div className='flex items-start justify-between gap-2 mb-1'>
        <BadgeWithDot color={statusColor} size='sm'>
          {statusLabel}
        </BadgeWithDot>
        {relativeTime && (
          <span className='text-xs text-tertiary'>{relativeTime}</span>
        )}
      </div>

      <p className='text-sm text-secondary line-clamp-2 mt-2'>
        {summarySnippet}
      </p>

      {research.created_at && (
        <p className='mt-2 text-xs text-tertiary'>
          <DateTimeValueLabel value={research.created_at} fontSize='xs' />
        </p>
      )}
    </button>
  );
}
